import React, { useState } from "react"
import { isEmpty, uniqBy } from "lodash"
import "./_style.scss"
import { GatsbyImage } from "gatsby-plugin-image"

// Swiper
import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination, Navigation } from "swiper"
import "swiper/css"
import "swiper/css/pagination"
import "swiper/css/navigation"

// Atoms
import RulerTitle from "§/src/components/atoms/title/ruler-title"
import ButtonEdgeRounded from "§/src/components/atoms/buttons/button-edge-rounded"
import ButtonArrow from "§/src/components/atoms/buttons/button-arrow"

// Utils
import UniversalLink from "§/src/utils/UniversalLink"

// Language
import { useTranslation } from "react-i18next"

const CaseHistoryCarousel = (props) => {
  const { t } = useTranslation()
  const slider = props.data || []
  const filterOn = props?.filter !== "false" ? true : false
  const rulerTitle = props.title || t("Contenuti correlati")

  const compClassName = ""
  const compClass =
    props?.className !== undefined
      ? compClassName + " " + props.className
      : compClassName

  const sliderId = "prod-" + Math.floor(Math.random() * 9999)

  // Gestione sicura di uniqueFilters
  const hasValidSlider = Array.isArray(slider) && slider.length > 0

  // Crea un array defaultFilters con valore sicuro
  const defaultFilters = (hasValidSlider && slider.length > 1)
    ? [{ label: t("Mostra tutti"), value: "" }]
    : []

  // Crea filters solo se slider è valido
  const contentFilters = hasValidSlider
    ? slider.map((caseHistory) => ({
        label: t(caseHistory?.applicationContents?.nodes?.[0]?.name || ''),
        value: caseHistory?.applicationContents?.nodes?.[0]?.name || '',
      }))
    : []

  // Unisci ed elimina duplicati solo se ci sono filtri
  const uniqueFilters = hasValidSlider
    ? uniqBy(
        [...defaultFilters, ...contentFilters].filter(item => item.label && item.label !== ""),
        "value"
      )
    : []

  // Inizializza il filtro in modo sicuro
  const [filter, setFilter] = useState(uniqueFilters.length > 0 ? uniqueFilters[0]?.value || "" : "")

  // Gestori di eventi per i pulsanti di navigazione
  const handlePrevClick = (swiperInstance) => {
    if (swiperInstance && typeof swiperInstance.slidePrev === 'function') {
      swiperInstance.slidePrev()
    }
  }

  const handleNextClick = (swiperInstance) => {
    if (swiperInstance && typeof swiperInstance.slideNext === 'function') {
      swiperInstance.slideNext()
    }
  }

  // Assicurati che slider sia un array non vuoto
  if (!hasValidSlider) {
    return null
  }

  return (
    <div comp="case-history-carousel" className={compClass}>
      <RulerTitle pretitle="Focus on" title={rulerTitle} />

      {filterOn && uniqueFilters.length > 0 && (
        <div className="tag-filters">
          {uniqueFilters.map(({ label, value }, index) => {
            return (
              <TagFilter
                key={index}
                label={label}
                value={value}
                className="inverted"
                onClick={() => setFilter(value)}
                isActive={filter === value}
              />
            )
          })}
        </div>
      )}

      <Swiper
        slidesPerView={1.25}
        spaceBetween={20}
        loop={hasValidSlider && slider.length > 1}
        watchOverflow={true}
        breakpoints={{
          576: {
            slidesPerView: 1.5,
          },
          768: {
            slidesPerView: 1.75,
          },
          992: {
            slidesPerView: 2.5,
          },
        }}
        pagination={{
          el: "#" + sliderId + "-pagination",
          type: "progressbar",
        }}
        navigation={{
          nextEl: "#" + sliderId + "-nav-next",
          prevEl: "#" + sliderId + "-nav-prev",
        }}
        modules={[Pagination, Navigation]}
        className="ch-swiper"
        id={sliderId}
      >
        {slider
          .filter((caseHistory) => {
            // Verifica se il filtro è vuoto o undefined
            if (!filter || filter === "") {
              return true
            }
            // Verifica che le proprietà necessarie esistano
            const categoryName = caseHistory?.applicationContents?.nodes?.[0]?.name
            return categoryName === filter
          })
          .map((caseHistory, pageIndex) => {
            const relatedImage = caseHistory?.acfPages_technology?.relatedPageImage?.gatsbyImage
            const featuredImage = caseHistory?.featuredImage?.node?.gatsbyImage
            const hasImage = relatedImage || featuredImage

            const relatedAltText = caseHistory?.acfPages_technology?.relatedPageImage?.altText || ""
            const featuredAltText = caseHistory?.featuredImage?.altText || ""
            const altText = relatedAltText || featuredAltText || ""

            const backgroundColor = caseHistory?.acfPages_technology?.backgroundColor
            const parentTitle = caseHistory?.wpParent?.node?.title
            const title = caseHistory?.title || ""
            const uri = caseHistory?.uri || "#"

            return (
              <SwiperSlide key={pageIndex}>
                {hasImage && (
                  <GatsbyImage
                    className="sw-image"
                    image={relatedImage || featuredImage}
                    objectFit="cover"
                    alt={altText}
                  />
                )}
                <div className="slide-container">
                  {backgroundColor && (
                    <div
                      className="slide-content-bg"
                      style={{background: backgroundColor}}
                    ></div>
                  )}
                  <div className="slide-content">
                    {parentTitle && (
                      <TagFilter label={parentTitle} />
                    )}
                    {title && <h4 className="ch-title">{title}</h4>}
                    <UniversalLink
                      text={t("Approfondisci")}
                      className="link-button"
                      to={uri}
                    >
                      <ButtonArrow
                        dir="end"
                        text={t("Approfondisci")}
                        className={"text-white"}
                      />
                    </UniversalLink>
                  </div>
                </div>
              </SwiperSlide>
            )
          })}

        <div className="sw-navigation">
          <ButtonEdgeRounded
            id={sliderId + "-nav-prev"}
            dir="start"
          />
          <ButtonEdgeRounded
            id={sliderId + "-nav-next"}
            dir="end"
          />
        </div>
      </Swiper>

      <div
        id={sliderId + "-pagination"}
        className="gefran-swiper-pagination-progressbar position-relative mt-4 mt-md-5"
      ></div>
    </div>
  )
}

const TagFilter = ({ label, isActive, value, className, onClick }) => {
  // Verifica se label esiste prima di renderizzare
  if (!label) return null

  return (
    <div
      className={`ch-category ${isActive ? " active" : ""} ${
        className || ""
      }`}
      onClick={onClick}
    >
      {label}
    </div>
  )
}

export default CaseHistoryCarousel
